export enum Priority {
  HIGH = 1,
  LOW = 2,
}

type Logger = (msg: string, data?: Record<string, any>) => void;

type Task<T> = {
  id: number;
  name: string; // 👈 for debugging
  priority: Priority;
  createdAt: number;
  startedAt?: number;
  endedAt?: number;
  run: (signal?: AbortSignal) => Promise<T>;
  resolve: (v: T) => void;
  reject: (e: any) => void;
  signal?: AbortSignal;
  meta?: Record<string, any>; // 👈 optional extra info
};

type EnqueueOpts = {
  signal?: AbortSignal;
  name?: string;
  meta?: Record<string, any>;
};

export class RequestScheduler {
  private concurrency: number;
  private running = 0;
  private q: Task<any>[] = [];
  private idSeq = 0;
  private logger?: Logger;
  private debug = false;

  constructor(concurrency = 1) {
    this.concurrency = Math.max(1, concurrency);
  }

  setConcurrency(n: number) {
    this.concurrency = Math.max(1, n);
  }

  enableDebug(debug = true, logger: Logger = (m, d) => console.debug(m, d)) {
    this.debug = debug;
    this.logger = logger;
  }

  enqueue<T>(
    run: (signal?: AbortSignal) => Promise<T>,
    priority: Priority = Priority.LOW,
    opts: EnqueueOpts = {}
  ): Promise<T> {
    const { signal, name, meta } = opts;

    if (signal?.aborted) {
      return Promise.reject(new DOMException("Aborted", "AbortError"));
    }

    return new Promise<T>((resolve, reject) => {
      const task: Task<T> = {
        id: ++this.idSeq,
        name: name ?? run.name ?? `task-${this.idSeq}`, // 👈 default name
        priority,
        createdAt: Date.now(),
        run,
        resolve,
        reject,
        signal,
        meta,
      };

      if (signal) {
        const onAbort = () => {
          const idx = this.q.findIndex((t) => t.id === task.id);
          if (idx >= 0) this.q.splice(idx, 1);
          this.log("aborted (while queued)", task);
          reject(new DOMException("Aborted", "AbortError"));
        };
        signal.addEventListener("abort", onAbort, { once: true });
      }

      this.q.push(task);
      this.log("queued", task, { queued: this.q.length, running: this.running });
      this.process();
    });
  }

  private nextTask(): Task<any> | undefined {
    if (!this.q.length) return undefined;
    const highs = this.q.filter((t) => t.priority === Priority.HIGH);
    if (highs.length) {
      const chosen = highs.sort((a, b) => a.createdAt - b.createdAt)[0];
      this.q = this.q.filter((t) => t.id !== chosen.id);
      return chosen;
    }
    return this.q.shift();
  }

  private process() {
    while (this.running < this.concurrency) {
      const task = this.nextTask();
      console.log("NEW Task", task);
      if (!task) return;

      if (task.signal?.aborted) {
        this.log("aborted (before start)", task);
        task.reject(new DOMException("Aborted", "AbortError"));
        continue;
      }

      this.running++;
      task.startedAt = Date.now();
      this.log("started", task, { queued: this.q.length, running: this.running });

      Promise.resolve()
        .then(() => task.run(task.signal))
        .then((v) => task.resolve(v))
        .catch((e) => task.reject(e))
        .finally(() => {
          task.endedAt = Date.now();
          const dur = task.endedAt - (task.startedAt ?? task.createdAt);
          this.running--;
          this.log("finished", task, {
            durationMs: dur,
            queued: this.q.length,
            running: this.running,
          });
          this.process();
        });
    }
  }

  private log(event: string, task: Task<any>, extra?: Record<string, any>) {
    if (!this.debug || !this.logger) return;
    this.logger(`[rq-scheduler] ${event}`, {
      id: task.id,
      name: task.name,
      priority: task.priority === Priority.HIGH ? "HIGH" : "LOW",
      ...task.meta,
      ...extra,
    });
  }
}

// Singleton - increased concurrency for better performance with large datasets
export const requestScheduler = new RequestScheduler(3);

// Wrap a queryFn so it executes via the scheduler, with a friendly name.
export function queuedQueryFn<TArgs extends any[], T>(
  fn: (...args: TArgs) => Promise<T>,
  priority: Priority = Priority.LOW,
  name?: string,
  meta?: Record<string, any>
) {
  return (...args: TArgs & [{ signal?: AbortSignal }]) => {
    const maybeCtx = (args?.[0] as any) ?? {};
    const signal: AbortSignal | undefined = maybeCtx.signal;
    return requestScheduler.enqueue(() => fn(...(args as TArgs)), priority, {
      signal,
      name: name ?? fn.name,
      meta,
    });
  };
}
